import { isNil, omitBy } from 'lodash'

import { Response } from '@marketplace-web/shared/api-client'
import { getSelectedDynamicFiltersArgs } from 'data/api/transformers/arguments/get-selected-dynamic-filters-args'
import { transformCatalogFiltersModelToParams } from 'data/api/transformers/arguments/transform-catalog-filters-model-to-params'
import { findThumbnail } from '@marketplace-web/shared/utils'
import { calculateRating } from 'data/utils/user-rating'
import {
  getDominantColor,
  ItemPhotoModel,
  ItemThumbnailSize,
  transformItemPhotoDtos,
} from '@marketplace-web/domain/item-data'
import { ProductItemModel, transformItemBoxDto } from '@marketplace-web/domain/product-item-data'
import { getItemPhotosThumbnails, getItemThumbnail } from 'data/utils/item'
import { transformCurrencyAmountDtoOrString } from 'data/transformers/currency-amount'

import {
  ClosetModel,
  ClosetUserModel,
  ClosetDto,
  ClosetPromotionUserDto,
  PromotedClosetsListResp,
  GetClosetPromotionArgs,
  ClosetPromotionItemDto,
} from '../types/closet-promotions'

export const getClosetPromotionsArgsToParams = (args: GetClosetPromotionArgs) => {
  const staticArgs = {
    page: args.page,
    per_page: args.perPage,
    exclude_member_ids: args.excludedUserIds?.join(','),
    search_session_id: args.searchSessionId,
    screen_name: args.screenName,
    preview: args.isPreview,
    ...transformCatalogFiltersModelToParams(args),
  }

  return omitBy(
    { ...staticArgs, ...getSelectedDynamicFiltersArgs(args.selectedDynamicFilters) },
    isNil,
  )
}

export const transformClosetUser = (user: ClosetPromotionUserDto): ClosetUserModel => {
  return {
    id: user.id,
    name: user.login,
    image: findThumbnail(user.photo),
    rating: calculateRating(user.feedback_reputation),
    ratingCount: user.feedback_count,
    isFollowed: user.is_favourite,
    itemCount: user.item_count,
  }
}

export const transformCloset = (closet: ClosetDto): ClosetModel => {
  const { items, user } = closet

  return {
    user: transformClosetUser(user),
    items,
    showBanner: false,
  }
}

export const transformClosets = (data: Array<ClosetDto>): Array<ClosetModel> =>
  data.map(transformCloset)

export const transformClosetPromotionsResponse = (response: Response<PromotedClosetsListResp>) =>
  transformClosets(response.promoted_closets)

const transformClosetPromotionPhotos = (item: ClosetPromotionItemDto): Array<ItemPhotoModel> => {
  if (item.photos && item.photos.length > 0) {
    return transformItemPhotoDtos(item.photos)
  }

  if (!item.photo) return []

  return transformItemPhotoDtos([item.photo])
}

const tranaformClosetPromotionItemsThumbnails = (
  item: ClosetPromotionItemDto,
): Array<string> | undefined => {
  if (item.photos && item.photos.length > 0) {
    return getItemPhotosThumbnails(item.photos, ItemThumbnailSize.Large)
  }

  if (!item.photo) return undefined

  return getItemPhotosThumbnails([item.photo], ItemThumbnailSize.Large)
}

const tranaformClosetPromotionItemThumbnail = (item: ClosetPromotionItemDto): string | null => {
  if (item.photos && item.photos.length > 0) {
    return getItemThumbnail(item.photos, ItemThumbnailSize.Large)
  }

  if (!item.photo) return null

  return getItemThumbnail([item.photo], ItemThumbnailSize.Large)
}

const transformClosetPromotionItemDominantColor = (item: ClosetPromotionItemDto): string | null => {
  if (item.photos && item.photos.length > 0) {
    return getDominantColor(item.photos)
  }

  if (!item.photo) return null

  return getDominantColor([item.photo])
}

export const transformClosetPromotionItemDtoToProductItem = (
  item: ClosetPromotionItemDto,
): ProductItemModel => ({
  id: item.id,
  title: item.title,
  user: { id: item.user_id, isBusiness: item.business_user },
  url: item.url,
  favouriteCount: item.favourite_count,
  priceWithDiscount: item.discount
    ? transformCurrencyAmountDtoOrString(item.discount, item.currency)
    : null,
  price: transformCurrencyAmountDtoOrString(item.price, item.currency),
  serviceFee: item.service_fee
    ? transformCurrencyAmountDtoOrString(item.service_fee, item.currency)
    : null,
  totalItemPrice: item.total_item_price
    ? transformCurrencyAmountDtoOrString(item.total_item_price, item.currency)
    : undefined,
  badge: item.badge,
  isFavourite: item.is_favourite,
  thumbnailUrl: tranaformClosetPromotionItemThumbnail(item),
  thumbnailUrls: tranaformClosetPromotionItemsThumbnails(item),
  dominantColor: transformClosetPromotionItemDominantColor(item),
  photos: transformClosetPromotionPhotos(item),
  itemBox: item.item_box && transformItemBoxDto(item.item_box),
})

export const transformClosetPromotionItemDtosToProductItems = (
  items: Array<ClosetPromotionItemDto>,
): Array<ProductItemModel> => items.map(transformClosetPromotionItemDtoToProductItem)
